/**
 * Wern-Wood postcode delivery checker.
 *
 * Standard postcode information comes from this theme's delivery-rates.json asset.
 * When the customer has shippable items in their basket, Shopify's calculated
 * shipping-rate API is also queried so the current basket rate matches checkout.
 */

(function () {
  'use strict';

  const SELECTORS = {
    deliveryButton: '#ww-check',
    deliveryInput: '#ww-postcode',
    deliveryResult: '#ww-result',
    productEstimator: '.product-block-list__item--shipping .shipping-estimator',
    productButton: '.product-block-list__item--shipping .shipping-estimator [data-action="estimate-shipping"]'
  };

  const scriptUrl = (document.currentScript && document.currentScript.src) || '';
  const rateTableUrl = (() => {
    try {
      const url = new URL(scriptUrl, window.location.href);
      url.pathname = url.pathname.replace(/custom\.js$/, 'delivery-rates.json');
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch (e) {
      return '';
    }
  })();

  let rateTablePromise = null;

  function storefrontRoot() {
    let root = '/';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) root = window.Shopify.routes.root;
    else if (window.routes && window.routes.rootUrl) root = window.routes.rootUrl;
    if (!root.endsWith('/')) root += '/';
    return root;
  }

  function storefrontUrl(path) {
    return storefrontRoot() + String(path || '').replace(/^\//, '');
  }

  function normalisePostcode(value) {
    const clean = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (clean.length < 5 || clean.length > 7) return '';
    return clean.slice(0, -3) + ' ' + clean.slice(-3);
  }

  function postcodeKeys(postcode) {
    const parts = postcode.split(' ');
    const district = parts[0] || '';
    const inward = parts[1] || '';
    return {
      district,
      sector: inward ? district + ' ' + inward.charAt(0) : ''
    };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function money(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return null;
    return '£' + amount.toFixed(2);
  }

  function tidyMoney(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return null;
    return '£' + (Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2));
  }

  function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async function fetchRateTable() {
    if (!rateTablePromise) {
      rateTablePromise = (async () => {
        if (!rateTableUrl) throw new Error('Could not determine delivery rate-table URL');
        const response = await fetch(rateTableUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!response.ok) throw new Error('Could not load delivery rate table (' + response.status + ')');
        return response.json();
      })();
    }
    return rateTablePromise;
  }

  function resolveZone(postcode, table) {
    const rules = table && table.rules ? table.rules : {};
    const zones = table && table.zones ? table.zones : {};
    const keys = postcodeKeys(postcode);

    let raw;
    if (keys.sector && Object.prototype.hasOwnProperty.call(rules, keys.sector)) raw = rules[keys.sector];
    else if (keys.district && Object.prototype.hasOwnProperty.call(rules, keys.district)) raw = rules[keys.district];

    if (raw == null) return null;
    if (typeof raw === 'string') return zones[raw] || null;
    return raw;
  }

  function standardInfo(postcode, table) {
    const zone = resolveZone(postcode, table);
    if (!zone) return null;

    const vatRate = Number(table.vat_rate == null ? 0.2 : table.vat_rate);
    const baseExVat = Number(zone.price_ex_vat);
    const freeOver = Number(zone.free_over_ex_vat);
    const smallFee = Number(table.small_order_fee_ex_vat);
    const smallThreshold = Number(table.small_order_threshold_ex_vat);

    return {
      zone,
      baseExVat,
      baseIncVat: Number.isFinite(baseExVat) ? baseExVat * (1 + vatRate) : null,
      freeOver,
      smallFee,
      smallFeeIncVat: Number.isFinite(smallFee) ? smallFee * (1 + vatRate) : null,
      smallThreshold,
      fallback: table.fallback && table.fallback.message
        ? table.fallback.message
        : "We don't currently deliver to that postcode area. Contact us for a delivery quote."
    };
  }

  function shippingParams(postcode) {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', postcode);
    params.set('shipping_address[country]', 'United Kingdom');
    return params.toString();
  }

  async function fetchCart() {
    const response = await fetch(storefrontUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Could not load basket');
    return response.json();
  }

  async function fetchCheckoutShippingRates(postcode) {
    const query = shippingParams(postcode);
    const prepareUrl = storefrontUrl('cart/prepare_shipping_rates.json') + '?' + query;
    const asyncUrl = storefrontUrl('cart/async_shipping_rates.json') + '?' + query;

    const prepared = await fetch(prepareUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!prepared.ok) {
      let detail = '';
      try { detail = await prepared.text(); } catch (e) {}
      throw new Error('Shipping rate preparation failed (' + prepared.status + ')' + (detail ? ': ' + detail.slice(0, 180) : ''));
    }

    for (let attempt = 0; attempt < 14; attempt++) {
      if (attempt) await wait(350);

      const response = await fetch(asyncUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Shipping rate lookup failed (' + response.status + ')');

      const data = await response.json();
      if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
    }

    throw new Error('Shipping rate lookup timed out');
  }

  function renderMessage(mode, resultEl, message) {
    if (mode === 'delivery') resultEl.innerHTML = '<div class="ww-resultBox ww-resultBox--no">' + escapeHtml(message) + '</div>';
    else resultEl.innerHTML = '<p>' + escapeHtml(message) + '</p>';
  }

  function standardRows(info) {
    const rows = [];

    if (Number.isFinite(info.baseExVat)) {
      let delivery = tidyMoney(info.baseExVat) + ' + VAT';
      if (Number.isFinite(info.baseIncVat)) delivery += ' (' + money(info.baseIncVat) + ' inc. VAT)';
      rows.push({ label: 'Standard delivery', value: delivery, free: false });
    }

    if (Number.isFinite(info.freeOver)) {
      rows.push({ label: 'Free delivery', value: 'Orders over ' + tidyMoney(info.freeOver) + ' + VAT', free: true });
    }

    if (Number.isFinite(info.smallFee) && Number.isFinite(info.smallThreshold)) {
      let surcharge = tidyMoney(info.smallFee) + ' + VAT';
      if (Number.isFinite(info.smallFeeIncVat)) surcharge += ' (' + money(info.smallFeeIncVat) + ' inc. VAT)';
      surcharge += ' on delivery orders under ' + tidyMoney(info.smallThreshold) + ' + VAT';
      rows.push({ label: 'Small order charge', value: surcharge, free: false });
    }

    return rows;
  }

  function liveRows(rates) {
    return rates.map(rate => {
      const numeric = Number(rate.price);
      return {
        label: escapeHtml(rate.name || 'Delivery'),
        value: numeric === 0 ? 'FREE' : (money(rate.price) || escapeHtml(rate.price)),
        free: numeric === 0
      };
    });
  }

  function renderResult(postcode, info, rates, mode, resultEl, liveAttempted, liveFailed) {
    const standard = standardRows(info);
    const live = Array.isArray(rates) ? liveRows(rates) : [];

    if (mode === 'delivery') {
      let html = '<div class="ww-resultBox ww-resultBox--ok">';
      html += '<div><strong>We deliver to ' + escapeHtml(postcode) + '.</strong></div>';

      if (live.length) {
        html += '<div class="ww-small" style="margin-top:10px;font-weight:700;">Your current basket</div>';
        html += '<div class="ww-priceRow">' + live.map(row => '<span class="ww-pricePill' + (row.free ? ' ww-pricePill--free' : '') + '">' + row.label + ': ' + row.value + '</span>').join('') + '</div>';
      }

      html += '<div class="ww-small" style="margin-top:12px;font-weight:700;">Delivery terms for this postcode</div>';
      html += '<div class="ww-priceRow">' + standard.map(row => '<span class="ww-pricePill' + (row.free ? ' ww-pricePill--free' : '') + '">' + escapeHtml(row.label) + ': ' + escapeHtml(row.value) + '</span>').join('') + '</div>';

      if (!liveAttempted) {
        html += '<div class="ww-small">These are the standard postcode rates. Add items to your basket and check again to see the live checkout rate for that basket.</div>';
      } else if (liveFailed) {
        html += '<div class="ww-small">The standard postcode rates are shown above. We could not retrieve the live basket rate just now; checkout will confirm the final charge.</div>';
      } else if (liveAttempted && !live.length) {
        html += '<div class="ww-small">No live online delivery option was returned for the current basket. The postcode itself is covered; contact us if you need us to check the order.</div>';
      } else {
        html += '<div class="ww-small">The current-basket rate is calculated by the same shipping service used at checkout.</div>';
      }

      html += '</div>';
      resultEl.innerHTML = html;
      return;
    }

    let html = '<p><strong>We deliver to ' + escapeHtml(postcode) + '.</strong></p>';

    if (live.length) {
      html += live.map(row => '<p><strong>' + row.label + ': ' + row.value + '</strong></p>').join('');
    }

    html += standard.map(row => '<p><strong>' + escapeHtml(row.label) + ':</strong> ' + escapeHtml(row.value) + '</p>').join('');

    if (!liveAttempted) {
      html += '<p style="font-size:11px;opacity:.65;margin-top:6px;">Standard postcode rates shown. Add items to your basket and check again for the live checkout rate.</p>';
    } else if (liveFailed) {
      html += '<p style="font-size:11px;opacity:.65;margin-top:6px;">Standard postcode rates shown. The live basket rate could not be retrieved just now; checkout will confirm the final charge.</p>';
    } else if (liveAttempted && !live.length) {
      html += '<p style="font-size:11px;opacity:.65;margin-top:6px;">This postcode is covered, but no live online delivery option was returned for the current basket.</p>';
    } else {
      html += '<p style="font-size:11px;opacity:.65;margin-top:6px;">Current-basket rate calculated by the same shipping service used at checkout.</p>';
    }

    resultEl.innerHTML = html;
  }

  async function checkShipping(mode, input, resultEl, productMeta) {
    const postcode = normalisePostcode(input.value);
    if (!postcode) {
      renderMessage(mode, resultEl, 'Please enter a valid UK postcode, for example SA11 2JX.');
      resultEl.style.display = 'block';
      return;
    }

    input.value = postcode;
    resultEl.style.display = 'block';
    renderMessage(mode, resultEl, 'Checking delivery…');

    if (productMeta && typeof window.gtag === 'function') {
      window.gtag('event', 'shipping_estimator_click', {
        product_id: productMeta.id || null,
        product_title: productMeta.title || null,
        postcode_area: postcode.split(' ')[0]
      });
    }

    let table;
    try {
      table = await fetchRateTable();
    } catch (error) {
      console.error('[Wern-Wood shipping checker] rate table', error);
      renderMessage(mode, resultEl, 'We could not load delivery information right now. Please try again or contact us.');
      return;
    }

    const info = standardInfo(postcode, table);
    if (!info) {
      const fallback = table.fallback && table.fallback.message
        ? table.fallback.message
        : "We don't currently deliver to that postcode area. Contact us for a delivery quote.";
      renderMessage(mode, resultEl, fallback);
      return;
    }

    let cart = null;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.warn('[Wern-Wood shipping checker] basket lookup failed', error);
    }

    const hasShippableBasket = !!(cart && cart.item_count && cart.requires_shipping);
    if (!hasShippableBasket) {
      renderResult(postcode, info, null, mode, resultEl, false, false);
      return;
    }

    try {
      const rates = await fetchCheckoutShippingRates(postcode);
      renderResult(postcode, info, rates, mode, resultEl, true, false);
    } catch (error) {
      console.error('[Wern-Wood shipping checker] live rate', error);
      renderResult(postcode, info, null, mode, resultEl, true, true);
    }
  }

  function deliveryContext() {
    const input = document.querySelector(SELECTORS.deliveryInput);
    const result = document.querySelector(SELECTORS.deliveryResult);
    return input && result ? { input, result } : null;
  }

  function productContext(button) {
    const estimator = button && button.closest(SELECTORS.productEstimator);
    if (!estimator) return null;
    const input = estimator.querySelector('input[name="postcode"]');
    const result = estimator.querySelector('.shipping-estimator__results');
    if (!input || !result) return null;
    return {
      input,
      result,
      meta: {
        id: estimator.dataset.productId || '',
        title: estimator.dataset.productTitle || ''
      }
    };
  }

  // Detach the delivery page's legacy checker. Its closure keeps references to the old
  // nodes, while the visible cloned nodes are controlled only by this handler.
  function neutraliseLegacyDeliveryChecker() {
    [SELECTORS.deliveryInput, SELECTORS.deliveryButton, SELECTORS.deliveryResult].forEach(selector => {
      const node = document.querySelector(selector);
      if (node && node.parentNode) node.replaceWith(node.cloneNode(true));
    });
  }

  // The old product estimator initializes on DOMContentLoaded by looking for data-rates-url.
  // Remove that hook before DOMContentLoaded, while retaining the estimator markup.
  function neutraliseLegacyProductEstimator() {
    document.querySelectorAll(SELECTORS.productEstimator + '[data-rates-url]').forEach(estimator => {
      estimator.removeAttribute('data-rates-url');
    });
  }

  function updateDeliveryPageCopy() {
    const checker = document.querySelector('.ww-card--checker');
    if (checker) {
      const lead = checker.querySelector('.ww-lead');
      if (lead) lead.textContent = 'Enter your postcode to see the delivery charge, free delivery threshold and small order charge. If you already have items in your basket, we’ll also check the live checkout rate.';
    }

    const confidenceCards = document.querySelectorAll('.ww-hero .ww-card');
    if (confidenceCards.length > 1) {
      const lead = confidenceCards[1].querySelector('.ww-lead');
      if (lead) lead.textContent = 'Your postcode sets the standard delivery charge and free delivery threshold. When your basket contains items, we also check the live rate returned to Shopify checkout.';
      const small = confidenceCards[1].querySelector('.ww-small');
      if (small) small.textContent = 'You can check a postcode before you start shopping; checkout will always confirm the final rate for the completed basket.';
    }

    const howItWorks = document.querySelector('.ww-grid2 .ww-card:first-child .ww-list');
    if (howItWorks) {
      howItWorks.innerHTML = '<li>Enter your full postcode.</li><li>We show the standard delivery charge, free delivery threshold and small order charge for that postcode.</li><li>If you have items in your basket, we also ask Shopify for the live delivery rate for that basket.</li><li>Checkout confirms the final delivery charge when you order.</li>';
    }

    document.querySelectorAll('.ww-faq details').forEach(detail => {
      const summary = detail.querySelector('summary');
      if (summary && summary.textContent.trim() === 'Can delivery rates change?') {
        const body = summary.nextElementSibling;
        if (body) body.textContent = 'Yes. The checker uses the current delivery-rate table for postcode charges and thresholds, and checks Shopify’s live calculated rate when you already have items in your basket.';
      }
    });
  }

  neutraliseLegacyDeliveryChecker();
  neutraliseLegacyProductEstimator();

  document.addEventListener('click', function (event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const deliveryButton = target.closest(SELECTORS.deliveryButton);
    if (deliveryButton) {
      const ctx = deliveryContext();
      if (!ctx) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkShipping('delivery', ctx.input, ctx.result);
      return;
    }

    const productButton = target.closest(SELECTORS.productButton);
    if (productButton) {
      const ctx = productContext(productButton);
      if (!ctx) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkShipping('product', ctx.input, ctx.result, ctx.meta);
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.matches(SELECTORS.deliveryInput)) {
      const ctx = deliveryContext();
      if (!ctx) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkShipping('delivery', ctx.input, ctx.result);
      return;
    }

    if (target.matches(SELECTORS.productEstimator + ' input[name="postcode"]')) {
      const estimator = target.closest(SELECTORS.productEstimator);
      const button = estimator && estimator.querySelector('[data-action="estimate-shipping"]');
      const ctx = productContext(button);
      if (!ctx) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkShipping('product', ctx.input, ctx.result, ctx.meta);
    }
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    updateDeliveryPageCopy();

    const ctx = deliveryContext();
    if (!ctx) return;
    try {
      const postcode = new URLSearchParams(window.location.search).get('postcode');
      if (postcode) {
        ctx.input.value = postcode;
        checkShipping('delivery', ctx.input, ctx.result);
      }
    } catch (e) {}
  });
})();
